<template>
  <settings-modal
    :locked="isLoading"
    class="t-subscription-discount-add-modal"
  >
    <span
      slot="heading"
      class="font-weight-bold"
    >Add subscription discount plan</span>

    <v-layout row>
      <v-flex xs4>
        <v-text-field
          v-model="form.dateFrom"
          type="date"
          :min="today"
          label="Date from"
          class="t-date-from-input"
          :disabled="isLoading"
          :error-messages="getValidationErrorMessage('dateFrom')"
        />
        <v-text-field
          v-model="form.dateTill"
          type="date"
          :min="today"
          label="Date till"
          class="t-date-till-input"
          :disabled="isLoading"
          :error-messages="getValidationErrorMessage('dateTill')"
        />
        <v-text-field
          v-model="form.freeMonths"
          type="number"
          label="Free months"
          class="t-free-months-input"
          :disabled="isLoading"
          :error-messages="getValidationErrorMessage('freeMonths')"
          min="1"
        />
      </v-flex>
    </v-layout>

    <v-layout row>
      <v-flex xs6>
        <v-checkbox
          v-model="form.notify"
          label="Need to notify clients"
          class="mb-1"
          color="primary"
          hide-details
          @change="onNotifyChange"
        />
      </v-flex>
    </v-layout>

    <template v-if="form.notify">
      <v-layout row>
        <v-flex xs6>
          <v-menu
            ref="notificationTimePicker"
            v-model="isNotificationTimePickerVisible"
            :close-on-content-click="false"
            lazy
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="form.notificationTime"
                :disabled="isLoading"
                label="Notification time"
                readonly
                clearable
                hint="The time should be specified in UTC +0 time zone"
                persistent-hint
                class="t-notification-time-field"
                v-on="on"
              />
            </template>
            <v-time-picker
              v-if="isNotificationTimePickerVisible"
              v-model="form.notificationTime"
              format="24hr"
              class="t-notification-datetime-picker"
              @click:minute="$refs.notificationTimePicker.save(form.notificationTime)"
            />
          </v-menu>
        </v-flex>
      </v-layout>

      <v-layout row>
        <v-flex xs6>
          <v-autocomplete
            v-model="form.feeGroups"
            :items="feeGroupOptions"
            :error-messages="getValidationErrorMessage('feeGroups')"
            :disabled="isLoading"
            class="t-fee-groups-field"
            item-text="name"
            item-value="value"
            placeholder="Select fee groups"
            hint="All groups will be applied if nothing is selected"
            persistent-hint
            deletable-chips
            small-chips
            multiple
            regular
          >
            <template slot="prepend-item">
              <v-list-tile
                ripple
                @click="onSelectAllFeeGroupsOptionClick"
              >
                <v-list-tile-action>
                  <v-icon :color="isAllFeeGroupOptionsSelected ? 'primary' : undefined">{{
                    `${isAllFeeGroupOptionsSelected ? 'check_box' : 'check_box_outline_blank'}`
                  }}</v-icon>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>Select all</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-divider class="mt-1 mb-1" />
            </template>
          </v-autocomplete>
        </v-flex>
      </v-layout>

      <one-data-column :label-props="{ style: 'display: none;' }">
        <v-layout>
          <v-flex xs4>
            <div><b>Profile types</b></div>
            <div class="t-profile-types-field">
              <v-checkbox
                v-for="(profileTypeOption, index) in profileTypeOptions"
                :key="profileTypeOption.value"
                v-model="form.profileTypes"
                :error-messages="getValidationErrorMessage('profileTypes')"
                :disabled="isLoading"
                color="primary"
                multiple
                :label="profileTypeOption.name"
                :value="profileTypeOption.value"
                :hide-details="index < profileTypeOptions.length"
                :ripple="false"
              />
            </div>
          </v-flex>

          <v-flex>
            <div><b>Licenses</b></div>
            <div class="t-licenses-field">
              <v-checkbox
                v-for="(licenseOption, index) in licenseOptions"
                :key="licenseOption.value"
                v-model="form.licenses"
                :error-messages="getValidationErrorMessage('licenses')"
                :disabled="isLoading"
                multiple
                color="primary"
                :label="licenseOption.name"
                :value="licenseOption.value"
                :hide-details="index < licenseOptions.length"
                :ripple="false"
              />
            </div>
          </v-flex>
        </v-layout>
      </one-data-column>
    </template>

    <error-alert :error="error" />

    <template slot="actions">
      <v-btn
        class="t-save-button"
        color="primary"
        medium
        flat
        :disabled="!isFormFilled || isLoading"
        @click="onSaveButtonClick"
      >Save</v-btn>

      <v-btn
        class="t-cancel-button"
        color="primary"
        medium
        flat
        :disabled="isLoading"
        @click="onCloseButtonClick"
      >cancel</v-btn>
    </template>
  </settings-modal>
</template>

<script>
  import SettingsModal from '@/components/modals/SettingsModal.vue'
  import modal from '@/mixins/modal'
  import { mapActions, mapGetters } from 'vuex'
  import form from '@/mixins/form'
  import ErrorAlert from '@/components/ui/ErrorAlert.vue'
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import SubscriptionApi from '@/services/Api/Subscription'
  import flashMessages from '@/mixins/flashMessages'
  import { formatDate } from '@/utils/Date'
  import OneDataColumn from '@/components/ui/Data/OneDataColumn.vue'
  import AccountProfile from '@/models/AccountProfile'

  function formDataToRequest (formData) {
    const { notificationTime, ...otherFormData } = formData

    return {
      ...otherFormData,
      feeGroups: formData.feeGroups.length === 0 ? null : formData.feeGroups,
      notificationTime: notificationTime != null
        ? `${formData.dateFrom}T${notificationTime}:00Z`
        : null
    }
  }

  export default {
    name: 'SubscriptionDiscountAddModal',
    components: {
      OneDataColumn,
      ErrorAlert,
      SettingsModal
    },
    middleware: ['primaryFeeGroups'],
    mixins: [form, modal, flashMessages],
    data () {
      return {
        error: null,
        isLoading: false,
        isNotificationTimePickerVisible: false,
        form: {
          dateFrom: null,
          dateTill: null,
          freeMonths: null,
          notify: false,
          notificationTime: null,
          profileTypes: [],
          feeGroups: [],
          licenses: []
        },
        addSubscriptionDiscountApiRequest: new ApiRequest(
          SubscriptionApi.addSubscriptionDiscount
        )
      }
    },
    computed: {
      ...mapGetters('modal', ['modal']),
      ...mapGetters('primaryFeeGroups', ['primaryFeeGroups']),
      today () {
        return formatDate(new Date())
      },
      licenseOptions () {
        return AccountProfile.licenseOptions
      },
      profileTypeOptions () {
        return AccountProfile.typeOptions
      },
      feeGroupOptions () {
        return this.primaryFeeGroups
      },
      isAllFeeGroupOptionsSelected () {
        return this.feeGroupOptions.length === this.form.feeGroups.length
      },
      requiredFields () {
        const requiredFields = [
          'dateFrom',
          'dateTill',
          'freeMonths'
        ]

        if (this.form.notify) {
          requiredFields.push('notificationTime')
        }

        return requiredFields
      }
    },
    methods: {
      ...mapActions('modal', ['showModal', 'closeModal']),
      onSaveButtonClick () {
        this.setIsLoading(true)

        this.addSubscriptionDiscountApiRequest.execute(formDataToRequest(this.parsedFormInput))
          .then(() => this.showSuccessFlashMessage('Subscription discount has been successfully created.', {
            onComplete: this.modal.data.successCallback
          }))
          .catch((error) => {
            this.setError(error)
            this.setIsLoading(false)
          })
      },
      onNotifyChange (notify) {
        if (!notify) {
          this.setFormData({
            notificationTime: null,
            profileTypes: null,
            feeGroups: [],
            licenses: null
          })
        }
      },
      onSelectAllFeeGroupsOptionClick () {
        this.setFieldValue(
          'feeGroups',
          this.isAllFeeGroupOptionsSelected ? [] : this.feeGroupOptions.map((value) => value)
        )
      },
      onCloseButtonClick () {
        this.closeModal()
      },
      setIsLoading (isLoading) {
        this.$set(this, 'isLoading', isLoading)
      }
    }
  }
</script>
